import { useEffect, useRef, useState } from 'react';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { useTheme } from '@mui/material/styles';
import { useDatastore } from '../../hooks';
import SyncStatusPopover from './SyncStatusPopover';

interface FullSyncIndicatorProps {
  compact?: boolean;
}

/**
 * Always-visible header button showing DataStore sync status. Clicking opens
 * an inline popover with compact per-model stats, so users can watch sync
 * health from any page without visiting /settings.
 *
 * One-shot per session: when the app releases from the essential-data gate
 * while a background full sync is still running, auto-open the popover once and
 * pulse the button, so users see where to watch progress (and wait for green
 * before acting if they hit an issue). Guarded by sessionStorage so it does not
 * re-open on every navigation, and never fires when the store is already synced.
 */
export default function FullSyncIndicator({ compact = false }: FullSyncIndicatorProps) {
  const theme = useTheme();
  const { isDatastoreReady, isSyncing } = useDatastore();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('ftl.syncPopoverAutoOpened') === 'true') return;
    // Only auto-open while a full sync is still in progress (cold start). If the
    // store is already fully synced on mount, stay closed and never auto-open.
    if (!isDatastoreReady) {
      sessionStorage.setItem('ftl.syncPopoverAutoOpened', 'true');
      setOpen(true);
      setHighlight(true);
      const timer = setTimeout(() => setHighlight(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isDatastoreReady]);

  const showSyncing = !isDatastoreReady || isSyncing;
  const label = showSyncing ? (compact ? 'Syncing' : 'Full sync in progress') : compact ? 'Synced' : 'Data synced';
  const tooltipTitle = showSyncing
    ? 'DataStore is running a background full sync. Click for details.'
    : 'DataStore is up to date. Click for sync status and settings.';

  return (
    <>
      <Tooltip enterTouchDelay={0} title={tooltipTitle} arrow placement="bottom">
        <ButtonBase
          ref={buttonRef}
          onClick={() => setOpen(true)}
          aria-haspopup="true"
          aria-expanded={open}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 0.5 : 1,
            px: compact ? 0.8 : 2,
            py: compact ? 0.75 : 0.75,
            minHeight: 36,
            boxSizing: 'border-box',
            borderRadius: 2,
            backgroundColor: showSyncing ? 'rgba(255, 193, 7, 0.14)' : 'rgba(255, 255, 255, 0.08)',
            border: showSyncing ? '1px solid rgba(255, 193, 7, 0.35)' : '1px solid rgba(255, 255, 255, 0.18)',
            transition: 'box-shadow 0.3s ease-in-out, background-color 0.2s ease-in-out',
            boxShadow: highlight ? `0 0 0 3px ${theme.palette.primary.main}66` : 'none',
            animation: highlight ? 'pulseSyncButton 1.2s ease-in-out 2' : 'none',
            '@keyframes pulseSyncButton': {
              '0%, 100%': { boxShadow: `0 0 0 3px ${theme.palette.primary.main}66` },
              '50%': { boxShadow: `0 0 0 6px ${theme.palette.primary.main}33` }
            },
            '&:hover': {
              backgroundColor: showSyncing ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 255, 255, 0.16)'
            }
          }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: compact ? '0.75rem' : '0.875rem',
              fontWeight: 600,
              color: theme.palette.primary.contrastText,
              opacity: showSyncing ? 1 : 0.85
            }}>
            {label}
          </Typography>
          {showSyncing ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                animation: 'spin 1.3s linear infinite',
                '@keyframes spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' }
                }
              }}>
              <AutorenewIcon sx={{ fontSize: 16, color: '#FFB300' }} />
            </Box>
          ) : (
            <CloudDoneIcon sx={{ fontSize: 16, color: theme.palette.primary.contrastText, opacity: 0.85 }} />
          )}
        </ButtonBase>
      </Tooltip>
      <SyncStatusPopover open={open} anchorEl={buttonRef.current} onClose={() => setOpen(false)} />
    </>
  );
}
